import {
    ConfigConstants,
    CustomInjector,
    DisplayDecorator,
    DropdownModel,
    MATERIAL_SOURCE_CODE,
    MaterialModel,
    MaterialSourceModel,
    ModelOptionsLoaderService,
    NotificationModel,
    PartModel,
    TextModel,
    ValueHelperInstanceToken,
    ValueHelperInterface,
    ValueHelperModel,
    ConfigInstanceToken,
} from '@evorait/pace-core';

export default class ConfirmationAddMaterialModel {
    public newMaterial: MaterialModel;
    public materialType: DropdownModel<TextModel>;

    /**
     * Prepare a model for the Confirmation Add New Material Modal.
     *
     * @param notification the notification model
     * @class
     */
    public constructor(notification: NotificationModel) {
        const injector = CustomInjector.getInjector();
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        const valueHelperService: ValueHelperInterface = injector.get(ValueHelperInstanceToken);
        const configService = injector.get(ConfigInstanceToken);

        const part: PartModel = PartModel.build({});
        // setting default values which are required to create few dropdown values of material
        part.billing = <ConfigConstants.BILLABLE_CODE>configService.getAddMaterialBillingIndicator();
        part.billingChangeable = configService.getAddMaterialBillingChangeable();
        part.lsCrChangeable.value = '0';

        this.newMaterial = new MaterialModel(part, notification);
        // setting some default values
        this.newMaterial.quantity.value = 1;
        this.newMaterial.materialSource = new MaterialSourceModel(MATERIAL_SOURCE_CODE.OTHER);
        this.newMaterial.billableCode.options = {
            ...this.newMaterial.billableCode.options,
            disabled: !part.billingChangeable,
        };

        // setting model-options of input fields
        modelOptionsLoader.load(this.newMaterial.awbReturn, 'MaterialConsumptionAdd', 'AWB');
        modelOptionsLoader.load(
            this.newMaterial.materialIdentifier.materialNumber,
            'MaterialConsumptionAdd',
            'MaterialNumber',
        );
        this.newMaterial.materialIdentifier.materialNumber.options = {
            ...this.newMaterial.materialIdentifier.materialNumber.options,
            applicationIdentifier: ConfigConstants.ApplicationIdentifier.ProductIdentifier,
        };
        modelOptionsLoader.load(this.newMaterial.description, 'MaterialConsumptionAdd', 'Description');
        modelOptionsLoader.load(
            this.newMaterial.materialIdentifier.orderNumber,
            'MaterialConsumptionAdd',
            'OrderNumber',
        );
        modelOptionsLoader.load(
            this.newMaterial.materialIdentifier.orderPosition,
            'MaterialConsumptionAdd',
            'OrderPosition',
        );
        modelOptionsLoader.load(this.newMaterial.quantity, 'MaterialConsumptionAdd', 'Quantity');
        modelOptionsLoader.load(this.newMaterial.amount, 'MaterialConsumptionAdd', 'Price');
        modelOptionsLoader.load(this.newMaterial.billableCode, 'MaterialConsumptionAdd', 'Billable');

        this.newMaterial.amount.options = {
            ...this.newMaterial.amount.options,
            unit: configService.getAddMaterialCurrencyCode(),
        };
        const addMaterialTypeOptions = valueHelperService.getAddMaterialTypes();
        const addMaterialTypeEntries = addMaterialTypeOptions.map(
            (vh: ValueHelperModel) =>
                new DisplayDecorator<TextModel>(vh.descText, new TextModel('MaterialType', vh.code)),
        );
        this.materialType = new DropdownModel<TextModel>('MaterialType', addMaterialTypeEntries);
        // "Non-siemens part" should be selected by default (SHSNGMAA-463)
        this.materialType.selectByPredicate(
            (entry) => entry.model.value === ConfigConstants.nonSiemensPartMaterialTypeCode,
        );

        modelOptionsLoader.load(this.materialType, 'MaterialConsumptionAdd', 'MaterialType');

        // Condition to check if the new material can be set billable
        const addMaterialSelectBillableDefault = configService.getAddMaterialSelectBillableDefault();
        if (addMaterialSelectBillableDefault) {
            this.newMaterial.billableCode.selectByPredicate(
                (entry) => entry.value === ConfigConstants.BILLABLE_CODE.BILLABLE,
            );
        }
    }
}
